import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { MachineFile, MachineFileKind } from '@couch/shared';
import { useState } from 'react';
import { api, errorMessage, machineFileViewUrl } from '../../api/client';
import { formatBytes } from './format';
import { statusKey } from './StatusPanel';

type Filter = 'all' | MachineFile['media'];

const PAGE = 60;
const KINDS: readonly [MachineFileKind, string][] = [['output', 'Generated (output)'], ['input', 'Uploaded photos (input)']];
const FILTERS: readonly [Filter, string][] = [['all', 'All'], ['image', 'Images'], ['video', 'Videos'], ['audio', 'Audio']];

const files = (n: number) => `${n} ${n === 1 ? 'file' : 'files'}`;
const sizeOf = (list: readonly MachineFile[]) => list.reduce((sum, f) => sum + f.sizeBytes, 0);

function Choice<T extends string>({ legend, name, options, value, onChange }: {
  legend: string; name: string; options: readonly [T, string][]; value: T; onChange: (value: T) => void;
}) {
  return (
    <fieldset className="choice">
      <legend className="muted">{legend}</legend>
      {options.map(([key, label]) => (
        <label key={key}>
          <input type="radio" name={name} checked={value === key} onChange={() => { onChange(key); }} /> {label}
        </label>
      ))}
    </fieldset>
  );
}

function FileRow({ machineId, kind, file, checked, onToggle }: {
  machineId: string; kind: MachineFileKind; file: MachineFile; checked: boolean; onToggle: () => void;
}) {
  return (
    <li className="file-row">
      <input type="checkbox" aria-label={file.name} checked={checked} onChange={onToggle} />
      {file.media === 'image'
        ? <img src={machineFileViewUrl(machineId, kind, file.name)} alt={file.name} loading="lazy" width={48} height={48} />
        : <span className="file-kind muted">{file.media}</span>}
      <span className="file-name">{file.name}</span>
      <span className="muted">{formatBytes(file.sizeBytes)}</span>
      <span className="muted">{new Date(file.modifiedAt).toLocaleString()}</span>
    </li>
  );
}

/** Deletes the chosen files, then reloads the list and the free disk space. */
function useDeleteFiles(machineId: string, kind: MachineFileKind, onDone: () => void) {
  const queryClient = useQueryClient();
  const [done, setDone] = useState('');
  const remove = useMutation({
    mutationFn: (names: string[]) => api.deleteMachineFiles(machineId, { kind, names }),
    onSuccess: async (result) => {
      const gone = result.missing ? ` (${files(result.missing)} were already gone)` : '';
      setDone(`Deleted ${files(result.deleted)} — ${formatBytes(result.freedBytes)} freed.${gone}`);
      onDone();
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['machine-files', machineId] }),
        queryClient.invalidateQueries({ queryKey: statusKey(machineId) }),
      ]);
    },
  });
  return { done, setDone, remove };
}

/** Everything ComfyUI wrote or was given on this machine, to free disk space (doc 47). */
export function FilesPanel({ machineId }: { machineId: string }) {
  const [kind, setKind] = useState<MachineFileKind>('output');
  const [filter, setFilter] = useState<Filter>('all');
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [shown, setShown] = useState(PAGE);
  const reset = () => { setSelected(new Set()); setShown(PAGE); };
  const list = useQuery({ queryKey: ['machine-files', machineId, kind], queryFn: () => api.machineFiles(machineId, kind), retry: false });
  const { done, setDone, remove } = useDeleteFiles(machineId, kind, () => { setSelected(new Set()); });
  const all = list.data?.files ?? [];
  const visible = filter === 'all' ? all : all.filter((f) => f.media === filter);
  const page = visible.slice(0, shown);
  const chosen = all.filter((f) => selected.has(f.name));
  const toggle = (name: string) => { setSelected((s) => { const next = new Set(s); if (!next.delete(name)) next.add(name); return next; }); };
  const ask = () => {
    setDone('');
    const what = `${files(chosen.length)}, ${formatBytes(sizeOf(chosen))}`;
    if (window.confirm(`Delete ${what} from this machine? They cannot be brought back; gallery previews stay.`)) {
      remove.mutate(chosen.map((f) => f.name));
    }
  };
  return (
    <section className="panel" aria-labelledby="files-title">
      <h2 id="files-title">Files on this machine</h2>
      <Choice legend="Folder" name="files-kind" options={KINDS} value={kind} onChange={(k) => { setKind(k); reset(); setDone(''); }} />
      <Choice legend="Show" name="files-filter" options={FILTERS} value={filter} onChange={(f) => { setFilter(f); reset(); }} />
      {list.isPending && <p role="status">Loading…</p>}
      {list.isError && <p role="alert" className="form-error">{errorMessage(list.error)}</p>}
      {list.data && <p>{formatBytes(list.data.totalBytes)} in {files(all.length)}{list.data.truncated ? ' (only the newest 5000 are listed)' : ''}</p>}
      {done && <p role="status">{done}</p>}
      {remove.isError && <p role="alert" className="form-error">{errorMessage(remove.error)}</p>}
      {list.isSuccess && visible.length === 0 && <p className="muted">Nothing here.</p>}
      {visible.length > 0 && (
        <div className="file-actions">
          <button type="button" className="button-quiet" onClick={() => { setSelected(new Set(page.map((f) => f.name))); }}>Select all shown</button>
          <button type="button" disabled={chosen.length === 0 || remove.isPending} onClick={ask}>
            Delete selected ({files(chosen.length)}, {formatBytes(sizeOf(chosen))})
          </button>
        </div>
      )}
      <ul className="file-list">
        {page.map((f) => <FileRow key={f.name} machineId={machineId} kind={kind} file={f} checked={selected.has(f.name)} onToggle={() => { toggle(f.name); }} />)}
      </ul>
      {visible.length > shown && (
        <button type="button" className="button-quiet" onClick={() => { setShown((n) => n + PAGE); }}>Show {Math.min(PAGE, visible.length - shown)} more</button>
      )}
    </section>
  );
}
